import { Box, Stack, Typography } from '@mui/material';
import { TrackRequest, TrackRequestType } from 'models/TrackRequest';
import { Track } from 'models/Track';
import { parseKey, camelotColors } from 'utils/keyUtils';

export interface CompactRequestEntryProps {
  request: TrackRequest;
}

export function CompactRequestEntry({ request }: CompactRequestEntryProps) {
  const isTrackLike =
    request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;
  const title =
    isTrackLike
      ? (request.value as Track)?.title || (request.value as string)
      : (request.value as string);
  const artist =
    isTrackLike ? (request.value as Track)?.artist : undefined;

  const localTrack = request.localTrack;
  const rawKey = localTrack?.key;
  const rawBpm = localTrack?.bpm;

  const parsedKey = parseKey(rawKey);
  const keyDisplay = parsedKey ? parsedKey.formatted : (rawKey || '-');
  const bpmDisplay = rawBpm && rawBpm > 0 ? Math.round(rawBpm).toString() : '-';

  const colorInfo = parsedKey
    ? camelotColors[parsedKey.number]
    : { bg: '#424242', text: '#ffffff' };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        width: '100%',
        p: 0.5,
        alignItems: 'center',
        transition: 'background-color 0.2s',
        borderRadius: 1,
        '&:hover': {
          bgcolor: 'action.hover',
          '& .full-text-on-hover': {
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
          },
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colorInfo.bg,
          color: colorInfo.text,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          userSelect: 'none',
        }}
      >
        {/* Top Half: Key */}
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '9px',
            fontWeight: 800,
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            lineHeight: 1,
          }}
        >
          {keyDisplay}
        </Box>
        {/* Bottom Half: BPM */}
        <Box
          sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '9px',
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {bpmDisplay}
        </Box>
      </Box>
      <Stack sx={{ minWidth: 0, justifyContent: 'center' }}>
        <Typography
          noWrap
          className="full-text-on-hover"
          sx={{
            lineHeight: 1.2,
            fontWeight: 600,
            fontSize: '0.875rem',
          }}
        >
          {title}
        </Typography>
        {artist && (
          <Typography
            noWrap
            className="full-text-on-hover"
            variant="caption"
            sx={{
              lineHeight: 1.2,
              color: 'text.secondary',
              display: 'block',
            }}
          >
            {artist}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

